import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Icon, IconName } from './ui';
import {
  ApprovalActionType,
  ApprovalStatus,
  createApproval,
  getApproval,
} from '../lib/approvalsApi';
import './ApprovalWorkflow.css';
import { Trans, useTranslation } from 'react-i18next';

interface ApprovalWorkflowProps {
  apiUrl: string;
  /** The sensitive action being requested */
  actionType: ApprovalActionType;
  /** Short human title, e.g. "Deploy Agent" */
  title: string;
  /** The resource being acted on */
  resourceId?: string;
  resourceLabel?: string;
  /** Extra context shown in the summary and stored with the request */
  metadata?: Record<string, any>;
  /**
   * When true, the current user is authorised to perform the action directly.
   * They will see a "Proceed now" option in addition to "Request approval".
   */
  canPerformDirectly?: boolean;
  /** Executes the action immediately (only called when canPerformDirectly). */
  onPerformDirectly?: () => Promise<void> | void;
  /** Called when the modal should close. */
  onClose: () => void;
  /** Called after the approved action completes (direct or post-approval). */
  onCompleted?: () => void;
}

const ACTION_ICON: Record<ApprovalActionType, IconName> = {
  'deploy-agent': 'rocket',
  'modify-policy': 'shield',
  'export-dlp': 'download',
};

type Phase = 'compose' | 'submitting' | 'pending' | 'approved' | 'rejected' | 'done' | 'error';

/**
 * Professional approval dialog for sensitive operations. Adapts to the user's
 * role: authorised users can proceed immediately or route through approval for
 * an audit trail; others can only request approval and watch the live status.
 */
export const ApprovalWorkflow: React.FC<ApprovalWorkflowProps> = ({
  apiUrl,
  actionType,
  title,
  resourceId,
  resourceLabel,
  metadata,
  canPerformDirectly = false,
  onPerformDirectly,
  onClose,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('compose');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [approvalId, setApprovalId] = useState<string | null>(null);
  const [decisionNote, setDecisionNote] = useState<string | null>(null);

  // Poll for a decision while pending
  useEffect(() => {
    if (phase !== 'pending' || !approvalId) return;
    let active = true;
    const poll = async () => {
      try {
        const a = await getApproval(apiUrl, approvalId);
        if (!active) return;
        if (a.status === 'approved') {
          setDecisionNote(a.decisionNote || null);
          setPhase('approved');
        } else if (a.status === 'rejected') {
          setDecisionNote(a.decisionNote || null);
          setPhase('rejected');
        }
      } catch {
        /* keep polling */
      }
    };
    const interval = setInterval(poll, 4000);
    poll();
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [phase, approvalId, apiUrl]);

  const submitRequest = useCallback(async () => {
    setError(null);
    setPhase('submitting');
    try {
      const { approvalId: id } = await createApproval(apiUrl, {
        actionType,
        resourceId,
        resourceLabel,
        reason: reason.trim() || undefined,
        metadata,
      });
      setApprovalId(id);
      setPhase('pending');
    } catch (e: any) {
      setError(e.message || 'Failed to submit request');
      setPhase('error');
    }
  }, [apiUrl, actionType, resourceId, resourceLabel, reason, metadata]);

  const performNow = useCallback(async () => {
    if (!onPerformDirectly) return;
    setError(null);
    setPhase('submitting');
    try {
      await onPerformDirectly();
      setPhase('done');
      onCompleted?.();
    } catch (e: any) {
      setError(e.message || 'Action failed');
      setPhase('error');
    }
  }, [onPerformDirectly, onCompleted]);

  const runApprovedAction = useCallback(async () => {
    if (!onPerformDirectly) {
      // No local executor — approval alone is the outcome.
      setPhase('done');
      onCompleted?.();
      return;
    }
    setPhase('submitting');
    try {
      await onPerformDirectly();
      setPhase('done');
      onCompleted?.();
    } catch (e: any) {
      setError(e.message || 'Action failed after approval');
      setPhase('error');
    }
  }, [onPerformDirectly, onCompleted]);

  const icon = ACTION_ICON[actionType];

  const footer = (() => {
    switch (phase) {
      case 'compose':
        return (
          <>
            <button className="ent-btn ghost" onClick={onClose}>{t('components.approvalWorkflow.cancel')}</button>
            <button className="ent-btn" onClick={submitRequest}>
              <Icon name="bell" /> {t('components.approvalWorkflow.requestApproval')}
            </button>
            {canPerformDirectly && (
              <button className="ent-btn primary" onClick={performNow}>
                <Icon name="checkCircle" /> {t('components.approvalWorkflow.proceedNow')}
              </button>
            )}
          </>
        );
      case 'submitting':
        return <button className="ent-btn" disabled>{t('components.approvalWorkflow.working')}</button>;
      case 'pending':
        return <button className="ent-btn ghost" onClick={onClose}>{t('components.approvalWorkflow.close')}</button>;
      case 'approved':
        return (
          <>
            <button className="ent-btn ghost" onClick={onClose}>{t('components.approvalWorkflow.close')}</button>
            <button className="ent-btn primary" onClick={runApprovedAction}>
              <Icon name="checkCircle" /> {onPerformDirectly ? t('components.approvalWorkflow.executeApprovedAction') : t('components.approvalWorkflow.done')}
            </button>
          </>
        );
      case 'rejected':
      case 'done':
      case 'error':
        return <button className="ent-btn" onClick={onClose}>{t('components.approvalWorkflow.close')}</button>;
      default:
        return null;
    }
  })();

  return (
    <Modal title={title} icon={icon} onClose={onClose} footer={footer} maxWidth={520}>
      {/* Action summary */}
      <div className="apr-summary">
        <div className="apr-summary-row">
          <span className="apr-summary-label">{t('components.approvalWorkflow.action')}</span>
          <span className="apr-summary-value">{title}</span>
        </div>
        {resourceLabel && (
          <div className="apr-summary-row">
            <span className="apr-summary-label">{t('components.approvalWorkflow.resource')}</span>
            <span className="apr-summary-value apr-mono">{resourceLabel}</span>
          </div>
        )}
        {metadata && Object.keys(metadata).length > 0 && (
          <div className="apr-summary-row">
            <span className="apr-summary-label">{t('components.approvalWorkflow.details')}</span>
            <span className="apr-summary-value apr-meta">
              {Object.entries(metadata).map(([k, v]) => (
                <span key={k} className="apr-chip">{k}: {String(v)}</span>
              ))}
            </span>
          </div>
        )}
      </div>

      {/* Phase-specific body */}
      {phase === 'compose' && (
        <>
          <div className={`apr-notice ${canPerformDirectly ? 'apr-notice-info' : 'apr-notice-warn'}`}>
            <Icon name={canPerformDirectly ? 'info' : 'lock'} />
            <div>
              {canPerformDirectly ? (
                <>
                  <Trans i18nKey="components.approvalWorkflow.authorisedToProceed" components={{ b: <strong /> }} />
                </>
              ) : (
                <>
                  <Trans i18nKey="components.approvalWorkflow.sensitiveOperation" components={{ b: <strong /> }} />
                </>
              )}
            </div>
          </div>
          <div className="ent-field">
            <label>
              {t('components.approvalWorkflow.reason')} {canPerformDirectly ? t('components.approvalWorkflow.optional') : ''}
            </label>
            <textarea
              className="ent-textarea"
              rows={3}
              placeholder={t('components.approvalWorkflow.explainWhyThisChangeIsNeeded')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </>
      )}

      {phase === 'pending' && (
        <div className="apr-status apr-status-pending">
          <div className="apr-spinner" />
          <h3>{t('components.approvalWorkflow.awaitingApproval')}</h3>
          <p>
            {t('components.approvalWorkflow.yourRequestHasBeenSentTo')}
            <strong> {t('components.approvalWorkflow.approvals')}</strong> {t('components.approvalWorkflow.inboxLater')}
          </p>
        </div>
      )}

      {phase === 'approved' && (
        <div className="apr-status apr-status-approved">
          <div className="apr-status-icon"><Icon name="checkCircle" size={40} /></div>
          <h3>{t('components.approvalWorkflow.requestApproved')}</h3>
          {decisionNote && <p className="apr-note">“{decisionNote}”</p>}
          <p>{onPerformDirectly ? t('components.approvalWorkflow.youCanNowExecuteTheApproved') : t('components.approvalWorkflow.theActionHasBeenAuthorised')}</p>
        </div>
      )}

      {phase === 'rejected' && (
        <div className="apr-status apr-status-rejected">
          <div className="apr-status-icon"><Icon name="deny" size={40} /></div>
          <h3>{t('components.approvalWorkflow.requestRejected')}</h3>
          {decisionNote && <p className="apr-note">“{decisionNote}”</p>}
          <p>{t('components.approvalWorkflow.thisActionWasNotAuthorisedAnd')}</p>
        </div>
      )}

      {phase === 'done' && (
        <div className="apr-status apr-status-approved">
          <div className="apr-status-icon"><Icon name="checkBadge" size={40} /></div>
          <h3>{t('components.approvalWorkflow.completed')}</h3>
          <p>{t('components.approvalWorkflow.theActionWasPerformedSuccessfully')}</p>
        </div>
      )}

      {phase === 'error' && (
        <div className="apr-status apr-status-rejected">
          <div className="apr-status-icon"><Icon name="warning" size={40} /></div>
          <h3>{t('components.approvalWorkflow.somethingWentWrong')}</h3>
          <p className="apr-note">{error}</p>
        </div>
      )}
    </Modal>
  );
};

/**
 * Small inline pill for showing an approval status in tables/lists.
 */
export const ApprovalStatusBadge: React.FC<{ status: ApprovalStatus }> = ({ status }) => {
  const { t } = useTranslation();
  const map: Record<ApprovalStatus, { labelKey: string; cls: string; icon: IconName }> = {
    pending: { labelKey: 'components.approvalWorkflow.pending', cls: 'apr-badge-pending', icon: 'bell' },
    approved: { labelKey: 'components.approvalWorkflow.approved', cls: 'apr-badge-approved', icon: 'checkCircle' },
    rejected: { labelKey: 'components.approvalWorkflow.rejected', cls: 'apr-badge-rejected', icon: 'deny' },
  };
  const s = map[status];
  return (
    <span className={`apr-badge ${s.cls}`}>
      <Icon name={s.icon} size={13} /> {t(s.labelKey)}
    </span>
  );
};
